import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api, { getErrorMessage } from '@/services/api';
import {
    CartItem,
    CartCalculation,
    CartCalculationRequest,
    StockValidation,
    ApiResponse,
    Product,
} from '@/services/types';
import { API_ENDPOINTS, STORAGE_KEYS, MAX_CART_ITEMS } from '@/utils/constants';
import { maxDiscountOf } from '@/utils/discountAllocator';

/**
 * Initial State
 */

interface CartState {
    items: CartItem[];
    calculation: CartCalculation | null;
    customerName: string;
    customerPhone: string;
    notes: string;
    totalDiscount: number;
    orderType: OrderType;
    isCalculating: boolean;
    isValidating: boolean;
    error: string | null;
    lastSavedTimestamp: number | null;
    heldCartCount: number | null;
}

export type OrderType = 'DINE_IN' | 'TAKEAWAY' | 'DRIVE_THRU';

const loadBackup = (): Partial<CartState> => {
    try {
        const raw = localStorage.getItem(STORAGE_KEYS.CART_BACKUP);
        if (!raw) return {};
        const p = JSON.parse(raw);
        return {
            items: p.items || [],
            customerName: p.customerName || '',
            customerPhone: p.customerPhone || '',
            notes: p.notes || '',
            totalDiscount: p.totalDiscount || 0,
            orderType: p.orderType || 'DINE_IN',
            lastSavedTimestamp: p.timestamp || null,
        };
    } catch {
        return {};
    }
};

const initialState: CartState = {
    items: [],
    calculation: null,
    customerName: '',
    customerPhone: '',
    notes: '',
    totalDiscount: 0,
    orderType: 'DINE_IN',
    isCalculating: false,
    isValidating: false,
    error: null,
    lastSavedTimestamp: null,
    heldCartCount: null,
    ...loadBackup(),
};

// Calculate cart totals
export const calculateCart = createAsyncThunk(
    'cart/calculateCart',
    async (params: { shopId: number; request: CartCalculationRequest }, { rejectWithValue }) => {
        try {
            const response = await api.post<ApiResponse<CartCalculation>>(
                API_ENDPOINTS.CALCULATE_CART(params.shopId),
                params.request
            );
            return response.data.data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

// Validate stock
export const validateStock = createAsyncThunk(
    'cart/validateStock',
    async (params: { shopId: number; items: CartItem[] }, { rejectWithValue }) => {
        try {
            const response = await api.post<ApiResponse<StockValidation>>(
                API_ENDPOINTS.VALIDATE_STOCK(params.shopId),
                params.items
            );
            return response.data.data;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        addItem: (state, action: PayloadAction<Product>) => {
            const product = action.payload;

            const existingItem = state.items.find(
                (item) => item.compositeId === product.id
            );

            if (existingItem) {
                existingItem.quantity += 1;
                existingItem.name = product.name;
                existingItem.unitPrice = product.price;
            } else {
                if (state.items.length >= MAX_CART_ITEMS) {
                    state.error = `Cart is full (max ${MAX_CART_ITEMS} items)`;
                    return;
                }
                state.items.push({
                    compositeId: product.id,
                    name: product.name,
                    quantity: 1,
                    unitPrice: product.price,
                    discount: 0,
                    lineTotal: product.price,
                    isTaxable: product.isTaxable,
                    inStock: product.inStock,
                    availableQty: product.availableQty,
                    taxAmount: 0,
                    sku: product.sku,
                    barcode: product.barcode,
                    variantName: product.variantName ?? undefined,
                });
            }

            state.lastSavedTimestamp = Date.now();
        },

        updateQuantity: (state, action: PayloadAction<{ compositeId: number; quantity: number }>) => {
            const { compositeId, quantity } = action.payload;

            const item = state.items.find((item) => item.compositeId === compositeId);

            if (item) {
                if (quantity <= 0) {
                    state.items = state.items.filter((item) => item.compositeId !== compositeId);
                } else {
                    item.quantity = quantity;
                }
                state.lastSavedTimestamp = Date.now();
            }
        },

        updateItemNotes: (state, action: PayloadAction<{ compositeId: number; notes: string }>) => {
            const { compositeId, notes } = action.payload;
            const item = state.items.find((item) => item.compositeId === compositeId);
            if (item) {
                item.notes = notes;
                state.lastSavedTimestamp = Date.now();
            }
        },

        updateDiscount: (state, action: PayloadAction<{ compositeId: number; discount: number }>) => {
            const { compositeId, discount } = action.payload;

            const item = state.items.find((item) => item.compositeId === compositeId);

            if (item) {
                item.discount = Math.max(0, discount);
                state.lastSavedTimestamp = Date.now();
            }
        },

        removeItem: (state, action: PayloadAction<number>) => {
            state.items = state.items.filter((item) => item.compositeId !== action.payload);
            state.lastSavedTimestamp = Date.now();
        },

        clearCart: (state) => {
            state.items = [];
            state.calculation = null;
            state.customerName = '';
            state.customerPhone = '';
            state.notes = '';
            state.totalDiscount = 0;
            state.orderType = 'DINE_IN';
            state.error = null;
            state.lastSavedTimestamp = Date.now();

            localStorage.removeItem(STORAGE_KEYS.CART_BACKUP);
        },

        setOrderType: (state, action: PayloadAction<OrderType>) => {
            state.orderType = action.payload;
        },

        setCustomerName: (state, action: PayloadAction<string>) => {
            state.customerName = action.payload;
        },

        setCustomerPhone: (state, action: PayloadAction<string>) => {
            state.customerPhone = action.payload;
        },

        setNotes: (state, action: PayloadAction<string>) => {
            state.notes = action.payload;
        },

        setTotalDiscount: (state, action: PayloadAction<number>) => {
            state.totalDiscount = Math.max(0, action.payload);
            state.lastSavedTimestamp = Date.now();
        },

        saveCartBackup: (state) => {
            const backup = {
                items: state.items,
                customerName: state.customerName,
                customerPhone: state.customerPhone,
                notes: state.notes,
                totalDiscount: state.totalDiscount,
                orderType: state.orderType,
                timestamp: Date.now(),
            };

            localStorage.setItem(STORAGE_KEYS.CART_BACKUP, JSON.stringify(backup));
            state.lastSavedTimestamp = backup.timestamp;
        },

        loadHeldCart: (state, action: PayloadAction<{ items: CartItem[]; customerName?: string; customerPhone?: string; notes?: string; orderType?: OrderType }>) => {
            state.items = action.payload.items;
            state.customerName = action.payload.customerName || '';
            state.customerPhone = action.payload.customerPhone || '';
            state.notes = action.payload.notes || '';
            state.orderType = action.payload.orderType || 'DINE_IN';
            state.calculation = null;
            state.lastSavedTimestamp = Date.now();
        },

        clearError: (state) => {
            state.error = null;
        },

        setHeldCartCount:(state, action: PayloadAction<number>) => {
            state.heldCartCount = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(calculateCart.pending, (state) => {
                state.isCalculating = true;
                state.error = null;
            })
            .addCase(calculateCart.fulfilled, (state, action) => {
                state.isCalculating = false;
                state.calculation = action.payload;

                // Adopt the server's per-line ceilings so distributeDiscount can respect them.
                // Skip lines whose quantity moved while the request was in flight (cap is quantity-derived),
                // and only assign on change so Immer keeps item references stable.
                for (const item of state.items) {
                    const calc = action.payload.items.find((c) => c.compositeId === item.compositeId);
                    if (!calc || calc.quantity !== item.quantity) continue;
                    const cap = Math.max(0, calc.maxItemDiscount ?? 0);
                    if (item.maxItemDiscount !== cap) item.maxItemDiscount = cap;
                }

                // Clamp totalDiscount to what the cart can actually absorb
                const limit = Math.min(action.payload.maxDiscount ?? Infinity, maxDiscountOf(state.items));
                if (state.totalDiscount > limit) state.totalDiscount = limit;
            })
            .addCase(calculateCart.rejected, (state, action) => {
                state.isCalculating = false;
                state.error = action.payload as string;
            });

        builder
            .addCase(validateStock.pending, (state) => {
                state.isValidating = true;
                state.error = null;
            })
            .addCase(validateStock.fulfilled, (state, action) => {
                state.isValidating = false;

                if (!action.payload.available) {
                    state.error = action.payload.message || 'Some items are out of stock';
                }
            })
            .addCase(validateStock.rejected, (state, action) => {
                state.isValidating = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    addItem,
    updateQuantity,
    updateItemNotes,
    updateDiscount,
    removeItem,
    clearCart,
    setCustomerName,
    setCustomerPhone,
    setNotes,
    setTotalDiscount,
    setOrderType,
    saveCartBackup,
    loadHeldCart,
    clearError,
    setHeldCartCount,
} = cartSlice.actions;

export default cartSlice.reducer;